<template>  <div>
    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
      <div 
        v-for="duck in sortedDucks" 
        :key="duck.uuid"
        class="group relative transform-gpu"
        @click="$emit('duck-click', duck)"
      >
        <div class="aspect-square rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer touch-manipulation"
             :class="[
               duck.rarity ? `border-4 ${getRarityBorderClass(duck.rarity)} ${getRarityBorderShadow(duck.rarity)}` : '',
               duck.rarity ? 'ring-2 ring-offset-2' : ''
             ]"
             :style="duck.rarity ? `--tw-ring-color: ${getRarityRingColor(duck.rarity)}` : ''">
          <v-img
            :src="getDuckImageUrl(cruiseId, duck.uuid)"
            :aspect-ratio="1"
            cover
            class="transition-transform duration-200 group-hover:scale-105 will-change-transform h-full w-full object-cover"
          >
            <template v-slot:placeholder>
              <div class="bg-gray-200 h-full w-full flex items-center justify-center">
                <v-progress-circular indeterminate size="small" />
              </div>
            </template>
          </v-img>
          
          <!-- Duck ID Overlay - mobile optimized -->
          <div class="absolute top-1 left-1 bg-black/70 text-white px-1.5 py-0.5 rounded text-xs font-medium z-10">
            {{ duck.id }}
          </div>
        </div>

        <!-- Status and actions overlay - mobile optimized -->
        <div class="absolute bottom-1 sm:bottom-2 left-1/2 -translate-x-1/2 flex justify-center items-center gap-1 max-w-full px-1">
          <span class="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-medium shadow-md text-center flex-shrink-0"
              :class="duck.found ? 'bg-green-500 text-white' : 'bg-red-500 text-white'">
              {{ duck.found ? 'Found' : 'Hidden' }}
          </span>          <v-btn
            icon
            size="x-small"
            color="primary"
            variant="tonal"
            class="ml-1 min-w-0 flex-shrink-0 message-icon-btn"
            style="width: 24px; height: 24px;"
            @click.stop="handleMessageClick(duck.uuid)"
          >
            <v-icon size="12">mdi-message</v-icon>
            <v-badge
              v-if="duck.unreadMessageCount && duck.unreadMessageCount > 0"
              :content="duck.unreadMessageCount"
              color="error"
              dot
            ></v-badge>
          </v-btn>
        </div>
      </div>
    </div>
    
    <!-- Memo about responding to duck messages -->
    <div class="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm text-gray-600 flex items-start">
      <v-icon color="info" size="16" class="mr-2 mt-0.5 flex-shrink-0">mdi-information-outline</v-icon>
      <p>
        You can respond to duck messages by clicking on the conversation icon <v-icon size="14" color="primary" class="mx-1">mdi-message</v-icon> next to any duck in the list.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { DetailedDuck } from '@/interfaces/cruise';
import { getRarityBorderClass, getRarityBorderShadow } from '@/lib/utils';

const DEFAULT_DUCK_ICON = '/ducksImages/duckIcon.webp';
const imageCache = new Map<string, string>();

const props = defineProps({
  ducks: {
    type: Array as () => DetailedDuck[],
    required: true
  },
  cruiseId: {
    type: Number,
    required: true
  }
});

const emit = defineEmits(['duck-click', 'message-click']);

const getRarityRingColor = (rarity: 'COMMON' | 'RARE' | 'LEGENDARY') => {
  switch (rarity) {
    case 'COMMON':
      return 'rgb(34 197 94)'; // green-500
    case 'RARE':
      return 'rgb(59 130 246)'; // blue-500
    case 'LEGENDARY':
      return 'rgb(168 85 247)'; // purple-500
    default:
      return '';
  }
};

const handleMessageClick = (duckUuid: string) => {
  emit('message-click', duckUuid);
};

const sortedDucks = computed(() => {
  return [...props.ducks].sort((a, b) => Number(a.id) - Number(b.id));
});

const getDuckImageUrl = (cruiseId: number, duckUuid: string) => {
  const cacheKey = `${cruiseId}-${duckUuid}`;
  
  if (imageCache.has(cacheKey)) {
    return imageCache.get(cacheKey)!;
  }

  const duck = props.ducks.find(d => d.uuid === duckUuid);
  
  if (!duck) {
    return DEFAULT_DUCK_ICON;
  }
  
  let url = DEFAULT_DUCK_ICON;
  
  // If duck has rarity, use rarity-based image
  if (duck.rarity) {
    url = `/duckRarity/${duck.rarity}.webp`;
  } else if (duck.image) {
    // Fallback to individual duck image
    // Handle different image types:
    // 1. Data URLs (data:image/...)
    // 2. Regular URLs (/path/to/image or http://...)
    // 3. Base64 strings (convert to data URL)
    if (duck.image.startsWith('data:')) {
      url = duck.image;
    } else if (duck.image.startsWith('/') || duck.image.startsWith('http')) {
      url = duck.image;
    } else {
      // Assume it's base64 data
      url = `data:image/webp;base64,${duck.image}`;
    }
  }

  imageCache.set(cacheKey, url);
  return url;
};
</script>

<style scoped>
/* Style for the message icon button */
.message-icon-btn {
  transition: all 0.2s ease;
}

.message-icon-btn:hover {
  transform: scale(1.15);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Enhanced rarity border effects */
.border-green-400 {
  border-color: rgb(74 222 128) !important;
  box-shadow: 0 0 15px rgba(34, 197, 94, 0.3) !important;
}

.border-blue-400 {
  border-color: rgb(96 165 250) !important;
  box-shadow: 0 0 15px rgba(59, 130, 246, 0.3) !important;
}

.border-purple-400 {
  border-color: rgb(196 181 253) !important;
  box-shadow: 0 0 15px rgba(168, 85, 247, 0.3) !important;
}

/* Ring effect enhancement */
.ring-2 {
  --tw-ring-offset-width: 2px;
  --tw-ring-width: 2px;
  box-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color), var(--tw-shadow, 0 0 #0000);
}

/* Tooltip-like effect on hover */
.message-icon-btn:hover::after {
  content: "View messages";
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  white-space: nowrap;
  z-index: 10;
}

/* Info box styling */
.bg-blue-50 {
  background-color: rgba(239, 246, 255, 0.8);
}

/* Fade in animation for the info box */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.mt-4 {
  animation: fadeIn 0.5s ease-out;
}
</style>